/**
 * Minimal YouTube / Vimeo iframe adapters over postMessage (no external
 * player libraries). Used by MediaCoordinator to learn when an embedded
 * player starts/stops and to pause it.
 *
 * YouTube needs `enablejsapi=1` in the embed URL; it is added here if
 * missing (that reloads the iframe once). Vimeo's player always accepts
 * postMessage commands.
 */

const YT_ORIGIN = 'https://www.youtube.com';
const VIMEO_ORIGIN = 'https://player.vimeo.com';
const LISTEN_RETRY_MS = 1000;
const LISTEN_RETRIES = 10;

const YT_PLAYING = 1;

function parse(data) {
    if (typeof data !== 'string') return data;
    try {
        return JSON.parse(data);
    } catch (e) {
        return null;
    }
}

function providerOf(src) {
    if (/youtube(-nocookie)?\.com\/embed\//.test(src)) return 'youtube';
    if (/player\.vimeo\.com\/video\//.test(src)) return 'vimeo';
    return null;
}

export function withYouTubeApi(src) {
    try {
        const url = new URL(src, window.location.href);
        if (url.searchParams.get('enablejsapi') === '1') return src;
        url.searchParams.set('enablejsapi', '1');
        url.searchParams.set('origin', window.location.origin);
        return url.toString();
    } catch (e) {
        return src;
    }
}

function youtubePlayer(iframe, { onPlaying, onStopped }) {
    const src = withYouTubeApi(iframe.src);
    if (src !== iframe.src) iframe.src = src;

    let state = null;
    let muted = false;
    let heard = false;
    let retries = 0;
    let retryTimer = null;

    const post = (message) => {
        try {
            iframe.contentWindow && iframe.contentWindow.postMessage(JSON.stringify(message), YT_ORIGIN);
        } catch (e) {}
    };

    // Ask the player to start sending infoDelivery/onStateChange messages.
    const listen = () => {
        if (heard || retries++ >= LISTEN_RETRIES) return;
        post({ event: 'listening', id: iframe.id || 'e3-media', channel: 'widget' });
        post({ event: 'command', func: 'addEventListener', args: ['onStateChange'] });
        retryTimer = setTimeout(listen, LISTEN_RETRY_MS);
    };

    const report = () => {
        if (state === YT_PLAYING && !muted) onPlaying();
        else onStopped();
    };

    const onMessage = (event) => {
        if (event.source !== iframe.contentWindow) return;
        if (!/^https:\/\/www\.youtube(-nocookie)?\.com$/.test(event.origin)) return;
        const data = parse(event.data);
        if (!data) return;
        heard = true;

        if (data.event === 'onStateChange') {
            state = data.info;
            report();
        } else if (data.event === 'infoDelivery' && data.info) {
            let changed = false;
            if (typeof data.info.playerState === 'number' && data.info.playerState !== state) {
                state = data.info.playerState;
                changed = true;
            }
            if (typeof data.info.muted === 'boolean' && data.info.muted !== muted) {
                muted = data.info.muted;
                changed = true;
            }
            if (changed) report();
        }
    };

    const onLoad = () => {
        heard = false;
        retries = 0;
        clearTimeout(retryTimer);
        listen();
    };

    window.addEventListener('message', onMessage);
    iframe.addEventListener('load', onLoad);
    onLoad();

    return {
        pause: () => post({ event: 'command', func: 'pauseVideo', args: [] }),
        destroy: () => {
            clearTimeout(retryTimer);
            window.removeEventListener('message', onMessage);
            iframe.removeEventListener('load', onLoad);
        },
    };
}

function vimeoPlayer(iframe, { onPlaying, onStopped }) {
    const post = (message) => {
        try {
            iframe.contentWindow && iframe.contentWindow.postMessage(JSON.stringify(message), VIMEO_ORIGIN);
        } catch (e) {}
    };

    const subscribe = () => {
        ['play', 'pause', 'ended', 'finish'].forEach(value => post({ method: 'addEventListener', value }));
    };

    const onMessage = (event) => {
        if (event.source !== iframe.contentWindow || event.origin !== VIMEO_ORIGIN) return;
        const data = parse(event.data);
        if (!data) return;

        if (data.event === 'ready') subscribe();
        else if (data.event === 'play') onPlaying();
        else if (data.event === 'pause' || data.event === 'ended' || data.event === 'finish') onStopped();
    };

    window.addEventListener('message', onMessage);
    iframe.addEventListener('load', subscribe);
    subscribe();

    return {
        pause: () => post({ method: 'pause' }),
        destroy: () => {
            window.removeEventListener('message', onMessage);
            iframe.removeEventListener('load', subscribe);
        },
    };
}

/**
 * @returns {{pause: Function, destroy: Function} | null} null for iframes
 *          that are not YouTube/Vimeo players.
 */
export function attachIframePlayer(iframe, callbacks) {
    const provider = providerOf(String(iframe.src || ''));
    if (provider === 'youtube') return youtubePlayer(iframe, callbacks);
    if (provider === 'vimeo') return vimeoPlayer(iframe, callbacks);
    return null;
}
